// Planning calculation utilities

/**
 * Get the budget totals for a specific department from an outcome's department_budgets array.
 * Returns { capex, opex, total } — all zeros if no matching entry.
 */
export const getDepartmentBudgetTotal = (outcome, departmentName) => {
  if (!outcome || !departmentName) return { capex: 0, opex: 0, total: 0 };
  const entry = (outcome.department_budgets || []).find(db => db.department_name === departmentName);
  if (!entry) return { capex: 0, opex: 0, total: 0 };
  const capex = entry.capex || 0;
  const opex = entry.opex || 0;
  return { capex, opex, total: capex + opex };
};

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
};

export const calculatePlanBreakEven = (selectedOutcomes, outcomeDetails, planStatuses, outcomeAdjustments, allOutcomes = {}) => {
  let totalDevelopmentCost = 0;
  let totalAnnualSavings = 0;
  let weightedBreakEvenSum = 0;
  let totalWeight = 0;

  selectedOutcomes.forEach(outcomeId => {
    const planStatus = planStatuses[outcomeId] || "Under Consideration";
    if (planStatus === "Declined") return;

    const details = outcomeDetails[outcomeId];
    if (!details) return;

    // Development cost calculation with approved budget priority
    let outcomeDevelopmentCost = 0;
    
    // Priority 1: Check for approved budget from outcome
    const outcome = allOutcomes[outcomeId];
    if (outcome?.approved_budget && 
        (outcome.approved_budget.capex > 0 || outcome.approved_budget.opex > 0)) {
      outcomeDevelopmentCost = Math.round((outcome.approved_budget.capex || 0) + (outcome.approved_budget.opex || 0));
    }
    // Priority 2: Use manual adjustment if available
    else if (outcomeAdjustments[outcomeId] !== undefined) {
      outcomeDevelopmentCost = outcomeAdjustments[outcomeId];
    }
    // Priority 3: Calculate from cost estimation
    else if (details.costEstimation) {
      const costEstimation = details.costEstimation;
      
      const baseRoleCost = (costEstimation.roles || []).reduce((sum, role) => {
        const months = (role.dateRanges || []).reduce((m, dr) =>
          m + ((dr.endMonth || costEstimation.adjustedMonths || 6) - (dr.startMonth || 1) + 1) * ((dr.allocation || 100) / 100), 0
        );
        return sum + (role.rate || 0) * months;
      }, 0);

      const totalFixedCost = (costEstimation.fixedCosts || []).reduce((sum, cost) => {
        return sum + (cost.monthlyCost || 0) * (cost.duration || costEstimation.adjustedMonths || 6);
      }, 0);

      outcomeDevelopmentCost = Math.round(baseRoleCost + totalFixedCost);
    }
    
    totalDevelopmentCost += outcomeDevelopmentCost;

    // Annual savings from financial model
    if (details.financialModel && details.financialModel.roles) {
      const financialModel = details.financialModel;
      let outcomeAnnualSavings = 0;

      financialModel.roles.forEach(role => {
        const annualCost = (role.count || 0) * (role.avg_salary || 0) * ((role.time_percentage || 100) / 100);
        
        // Calculate total efficiency gain for this role
        let totalEfficiencyGain = 0;
        (financialModel.milestones || []).forEach(milestone => {
          const efficiencyGains = milestone.efficiency_gains || {};
          totalEfficiencyGain += efficiencyGains[role.title] || 0;
        });

        // Cap efficiency at 100%
        const cappedEfficiency = Math.min(totalEfficiencyGain, 100);
        const roleSavings = annualCost * (cappedEfficiency / 100);
        outcomeAnnualSavings += roleSavings;
      });

      totalAnnualSavings += outcomeAnnualSavings;

      // For weighted average, use the outcome's individual break-even
      if (outcomeAnnualSavings > 0) {
                // Use the same development cost calculation as above
        let outcomeDevelopmentCostForWeighted = 0;
        if (outcome?.approved_budget && 
            (outcome.approved_budget.capex > 0 || outcome.approved_budget.opex > 0)) {
          outcomeDevelopmentCostForWeighted = Math.round((outcome.approved_budget.capex || 0) + (outcome.approved_budget.opex || 0));
        } else if (outcomeAdjustments[outcomeId] !== undefined) {
          outcomeDevelopmentCostForWeighted = outcomeAdjustments[outcomeId];
        } else if (details.costEstimation) {
          const costEstimation = details.costEstimation;
          const baseRoleCost = (costEstimation.roles || []).reduce((sum, role) => {
            const months = (role.dateRanges || []).reduce((m, dr) =>
              m + ((dr.endMonth || costEstimation.adjustedMonths || 6) - (dr.startMonth || 1) + 1) * ((dr.allocation || 100) / 100), 0
            );
            return sum + (role.rate || 0) * months;
          }, 0);
          const totalFixedCost = (costEstimation.fixedCosts || []).reduce((sum, cost) => {
            return sum + (cost.monthlyCost || 0) * (cost.duration || costEstimation.adjustedMonths || 6);
          }, 0);
          outcomeDevelopmentCostForWeighted = Math.round(baseRoleCost + totalFixedCost);
        }

        const outcomeBreakEvenMonths = (outcomeDevelopmentCostForWeighted / outcomeAnnualSavings) * 12;
        weightedBreakEvenSum += outcomeBreakEvenMonths * outcomeAnnualSavings;
        totalWeight += outcomeAnnualSavings;
      }
    }
  });

  if (totalAnnualSavings === 0) return null;

  const simpleBreakEvenMonths = (totalDevelopmentCost / totalAnnualSavings) * 12;
  const weightedBreakEvenMonths = totalWeight > 0 ? weightedBreakEvenSum / totalWeight : simpleBreakEvenMonths;

  return {
    totalDevelopmentCost,
    totalAnnualSavings,
    simpleBreakEvenMonths,
    weightedBreakEvenMonths
  };
};

export const calculatePlanSavings = (selectedOutcomes, outcomeDetails, planStatuses, outcomeAdjustments, colaRate = 4.5, allOutcomes = {}) => {
  let totalCurrentCosts = 0;
  let totalFutureCosts = 0;
  let contributingOutcomes = 0;
  
  selectedOutcomes.forEach(outcomeId => {
    const planStatus = planStatuses[outcomeId] || "Under Consideration";
    if (planStatus === "Declined") return;
    
    const details = outcomeDetails[outcomeId];
    if (!details || !details.financialModel || !details.financialModel.roles) return;
    
    const financialModel = details.financialModel;
    let outcomeCurrentCosts = 0;
    let outcomeFutureCosts = 0;
    let hasValidData = false;
    
    // Calculate costs for each role
    financialModel.roles.forEach(role => {
      const annualCost = (role.count || 0) * (role.avg_salary || 0) * ((role.time_percentage || 100) / 100);
      if (annualCost > 0) {
        hasValidData = true;
        
        // Apply COLA to get next year's baseline cost
        const nextYearBaseCost = annualCost * (1 + (colaRate / 100));
        outcomeCurrentCosts += nextYearBaseCost;
        
        // Calculate efficiency gains for this role
        let totalEfficiencyGain = 0;
        (financialModel.milestones || []).forEach(milestone => {
          const efficiencyGains = milestone.efficiency_gains || {};
          totalEfficiencyGain += efficiencyGains[role.title] || 0;
        });
        
        // Cap efficiency at 100%
        const cappedEfficiency = Math.min(totalEfficiencyGain, 100);
        
        // Apply efficiency to get future cost, then apply COLA
        const efficiencyCost = annualCost * (1 - (cappedEfficiency / 100));
        const nextYearEfficiencyCost = efficiencyCost * (1 + (colaRate / 100));
        outcomeFutureCosts += nextYearEfficiencyCost;
      }
    });
    
    if (hasValidData) {
      totalCurrentCosts += outcomeCurrentCosts;
      totalFutureCosts += outcomeFutureCosts;
      contributingOutcomes++;
    }
  });
  
  const totalSavings = totalCurrentCosts - totalFutureCosts;
  const savingsPercentage = totalCurrentCosts > 0 ? (1 - (totalFutureCosts / totalCurrentCosts)) * 100 : 0;
  
  return {
    totalCurrentCosts,
    totalFutureCosts,
    totalSavings,
    savingsPercentage,
    contributingOutcomes,
    totalOutcomes: selectedOutcomes.filter(id => (planStatuses[id] || "Under Consideration") !== "Declined").length
  };
};